import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Building, Phone, Save, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const Profile = () => {
  const { user, token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    company: user?.company || '',
    phone: user?.phone || ''
  });

  // Estado para alteração de senha
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // TODO: Implementar atualização do perfil
    alert('Funcionalidade de atualização será implementada em breve');
    setEditing(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    // Validações
    if (passwordData.new_password.length < 6) {
      setPasswordMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 6 caracteres' });
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }

    setPasswordLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // Fecha o formulário após 2 segundos
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordMessage({ type: '', text: '' });
      }, 2000);

    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao alterar senha';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Meu Perfil</h1>
        <p className="text-gray-600">Gerencie suas informações pessoais</p>
      </div>

      <div className="bg-white rounded-lg shadow-md border-2 border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Informações Pessoais</h2>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-arcano-blue hover:underline font-semibold"
              data-testid="edit-profile-btn"
            >
              Editar
            </button>
          )}
        </div>

        <div className="p-6">
          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue"
                    data-testid="full-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue"
                    data-testid="company-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-arcano-blue text-white px-6 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors"
                  data-testid="save-profile-btn"
                >
                  <Save size={20} />
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-6 py-3 border-2 border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  data-testid="cancel-edit-btn"
                >
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Mail size={24} className="text-arcano-blue" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <User size={24} className="text-arcano-blue" />
                <div>
                  <p className="text-sm text-gray-600">Nome Completo</p>
                  <p className="font-semibold text-gray-900">{user?.full_name}</p>
                </div>
              </div>

              {user?.company && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <Building size={24} className="text-arcano-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Empresa</p>
                    <p className="font-semibold text-gray-900">{user.company}</p>
                  </div>
                </div>
              )}

              {user?.phone && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <Phone size={24} className="text-arcano-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Telefone</p>
                    <p className="font-semibold text-gray-900">{user.phone}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="bg-green-500 rounded-full p-1">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-green-700">Status</p>
                  <p className="font-semibold text-green-900">Conta Ativa</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security Section */}
      <div className="bg-white rounded-lg shadow-md border-2 border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Lock size={24} />
            Segurança
          </h2>
        </div>
        <div className="p-6">
          {!showPasswordForm ? (
            <button 
              onClick={() => setShowPasswordForm(true)}
              className="flex items-center gap-2 text-arcano-blue hover:underline font-semibold"
              data-testid="change-password-btn"
            >
              <Lock size={18} />
              Alterar Senha
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-6 max-w-md">
              {/* Mensagem de feedback */}
              {passwordMessage.text && (
                <div className={`flex items-center gap-2 p-4 rounded-lg ${
                  passwordMessage.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {passwordMessage.type === 'success' 
                    ? <CheckCircle size={20} /> 
                    : <AlertCircle size={20} />
                  }
                  {passwordMessage.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha Atual
                </label>
                <PasswordInput
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Digite sua senha atual"
                  required
                  name="current_password"
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nova Senha
                </label>
                <PasswordInput
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  required
                  name="new_password"
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nova Senha
                </label>
                <PasswordInput
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirme a nova senha"
                  required
                  name="confirm_password"
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex items-center gap-2 bg-arcano-blue text-white px-6 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-password-btn"
                >
                  {passwordLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Alterando...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Alterar Senha
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    setPasswordMessage({ type: '', text: '' });
                  }}
                  className="px-6 py-3 border-2 border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  data-testid="cancel-password-btn"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
